import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { imgUrl } from "@/lib/api";
import { startCheckout } from "@/lib/payments";
import { useAuth } from "@/context/AuthContext";
import PaymentMethods from "@/components/PaymentMethods";
import { UploadCloud, X, Tag, Loader2, Gift, Check, Pencil } from "lucide-react";
import { toast } from "sonner";

const CONDITIONS = ["Nieuw", "Zo goed als nieuw", "Gebruikt", "Beschadigd"];

export default function CreateListing() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = !!id;
  const { user } = useAuth();
  const [promo, setPromo] = useState(false);
  const freeEligible = promo || (user && !user.free_listing_used);
  const [categories, setCategories] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", price: "", category: "", condition: "Nieuw",
    location: "", images: [], contact_method: "chat", contact_email: "", contact_phone: "", delivery: "",
    pickup: true, shipping: false, shipping_cost: "",
  });

  useEffect(() => {
    api.get("/categories").then(({ data }) => {
      setCategories(data);
      if (!editing) setForm((f) => ({ ...f, category: data[0] }));
    });
    api.get("/settings").then(({ data }) => setPromo(!!data.free_promo)).catch(() => {});
  }, [editing]);

  useEffect(() => {
    if (!editing) return;
    api.get(`/products/${id}/manage`).then(({ data }) => {
      setForm({
        title: data.title || "", description: data.description || "",
        price: data.price != null ? String(data.price) : "",
        category: data.category || "", condition: data.condition || "Nieuw",
        location: data.location || "", images: data.images || [],
        contact_method: data.contact_method || "chat",
        contact_email: data.contact_email || "", contact_phone: data.contact_phone || "",
        delivery: data.delivery || "",
        pickup: data.pickup ?? true, shipping: data.shipping ?? false,
        shipping_cost: data.shipping_cost ? String(data.shipping_cost) : "",
      });
    }).catch((err) => {
      toast.error(err.response?.data?.detail || "Kon advertentie niet laden");
      navigate("/dashboard");
    });
  }, [editing, id, navigate]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleFiles = async (files) => {
    setUploading(true);
    try {
      const paths = [];
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(data.path);
      }
      setForm((f) => ({ ...f, images: [...f.images, ...paths] }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload mislukt");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (p) => setForm((f) => ({ ...f, images: f.images.filter((x) => x !== p) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.price || form.images.length < 2) {
      toast.error("Vul titel en prijs in en voeg minimaal 2 foto's toe");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        shipping_cost: form.shipping && form.shipping_cost !== "" ? parseFloat(form.shipping_cost) : 0,
      };
      if (editing) {
        await api.put(`/products/${id}`, payload);
        toast.success("Wijzigingen opgeslagen");
        navigate(`/product/${id}`);
        return;
      }
      const { data } = await api.post("/products", payload);
      if (data.free) {
        toast.success("Je eerste advertentie is gratis — direct gepubliceerd! 🎉");
        navigate(`/product/${data.id}`);
      } else {
        toast.success("Advertentie aangemaakt — rond de betaling af om te publiceren");
        await startCheckout("listing_fee", data.id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kon advertentie niet opslaan");
      setSaving(false);
    }
  };

  const input = "w-full rounded-lg border border-zinc-800 bg-zinc-900/60 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-rose-700 focus:outline-none focus:ring-1 focus:ring-rose-700";
  const label = "block text-sm font-medium text-zinc-300 mb-2";

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 py-10">
      <div className="flex items-center gap-2 mb-2">
        {editing ? <Pencil className="h-4 w-4 text-rose-400" /> : <Tag className="h-4 w-4 text-rose-400" />}
        <span className="font-mono text-xs uppercase tracking-widest text-rose-400">{editing ? "Advertentie bewerken" : "Nieuwe advertentie"}</span>
      </div>
      <h1 className="font-serif text-4xl font-semibold text-zinc-100 mb-8">{editing ? "Bewerk je advertentie" : "Plaats je product"}</h1>

      {!editing && (
        <button type="button" data-testid="switch-to-service-button" onClick={() => navigate("/dienst-plaatsen")} className="mb-6 w-full text-left rounded-xl border border-rose-900/50 bg-rose-950/20 px-4 py-3 text-sm text-rose-200 hover:bg-rose-950/40 transition-colors">
          Wil je een <span className="font-semibold">dienst</span> aanbieden (escort, massage, SM...)? Klik hier — gratis plaatsen →
        </button>
      )}

      {!editing && freeEligible && (
        <div data-testid="free-listing-banner" className="mb-6 flex items-center gap-3 rounded-xl border border-emerald-800/50 bg-emerald-950/30 px-4 py-3 animate-fade-up">
          <Gift className="h-5 w-5 text-emerald-400 shrink-0" />
          <span className="text-sm text-emerald-200">
            {promo
              ? <>🎉 <span className="font-semibold">Lanceeractie</span>: alle advertenties zijn nu <span className="font-semibold">gratis</span> te plaatsen!</>
              : <>Je eerste advertentie is <span className="font-semibold">gratis</span> — deze wordt direct gepubliceerd zonder betaling!</>}
          </span>
        </div>
      )}

      <form onSubmit={submit} className="space-y-6">
        {/* Images */}
        <div>
          <label className={label}>Foto's <span className="text-rose-400 font-normal">(minimaal 2)</span></label>
          <label
            data-testid="image-dropzone"
            className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-zinc-800 bg-zinc-900/40 py-10 cursor-pointer hover:border-rose-700/60 transition-colors"
          >
            {uploading ? <Loader2 className="h-6 w-6 text-rose-400 animate-spin" /> : <UploadCloud className="h-6 w-6 text-zinc-500" />}
            <span className="text-sm text-zinc-400">{uploading ? "Uploaden..." : "Klik of sleep afbeeldingen hierheen"}</span>
            <span className="text-xs text-zinc-600">Voeg minstens 2 duidelijke foto's toe</span>
            <input data-testid="image-upload-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
          </label>
          {form.images.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-3">
              {form.images.map((p) => (
                <div key={p} className="relative h-24 w-24 overflow-hidden rounded-lg border border-zinc-800">
                  <img src={imgUrl(p)} alt="" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => removeImage(p)} className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {form.images.length === 1 && (
            <p className="mt-2 text-xs text-amber-400">Nog 1 foto nodig — minimaal 2 foto's per advertentie.</p>
          )}
        </div>

        <div>
          <label className={label}>Titel</label>
          <input data-testid="listing-title-input" className={input} value={form.title} onChange={upd("title")} placeholder="bijv. Luxe massageolie set" required />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={label}>Prijs (€)</label>
            <input data-testid="listing-price-input" type="number" step="0.01" min="0" className={input} value={form.price} onChange={upd("price")} placeholder="0.00" required />
          </div>
          <div>
            <label className={label}>Categorie</label>
            <select data-testid="listing-category-select" className={input} value={form.category} onChange={upd("category")}>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className={label}>Staat van het artikel</label>
          <div data-testid="listing-condition-group" className="flex flex-wrap gap-2">
            {CONDITIONS.map((c) => {
              const active = form.condition === c;
              return (
                <button
                  key={c}
                  type="button"
                  data-testid={`condition-option-${c}`}
                  aria-pressed={active}
                  onClick={() => setForm((f) => ({ ...f, condition: c }))}
                  className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-all ${active ? "border-rose-600 bg-rose-600 text-white shadow-lg shadow-rose-950/40" : "border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:border-zinc-700 hover:text-zinc-100"}`}
                >
                  <span className={`flex h-4 w-4 items-center justify-center rounded-full border ${active ? "border-white bg-white" : "border-zinc-600"}`}>
                    {active && <Check className="h-3 w-3 text-rose-600" />}
                  </span>
                  {c}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className={label}>Locatie</label>
          <input data-testid="listing-location-input" className={input} value={form.location} onChange={upd("location")} placeholder="bijv. Amsterdam" />
        </div>

        <div>
          <label className={label}>Beschrijving</label>
          <textarea data-testid="listing-description-input" rows={5} className={input} value={form.description} onChange={upd("description")} placeholder="Beschrijf je product, staat en eventuele bijzonderheden..." required />
        </div>

        <div>
          <label className={label}>Verzendopties</label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="listing-pickup-toggle"
              aria-pressed={form.pickup}
              onClick={() => setForm((f) => ({ ...f, pickup: !f.pickup }))}
              className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-all ${form.pickup ? "border-rose-600 bg-rose-600 text-white" : "border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:border-zinc-700"}`}
            >
              <span className={`flex h-4 w-4 items-center justify-center rounded-full border ${form.pickup ? "border-white bg-white" : "border-zinc-600"}`}>{form.pickup && <Check className="h-3 w-3 text-rose-600" />}</span>
              Ophalen mogelijk
            </button>
            <button
              type="button"
              data-testid="listing-shipping-toggle"
              aria-pressed={form.shipping}
              onClick={() => setForm((f) => ({ ...f, shipping: !f.shipping }))}
              className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-all ${form.shipping ? "border-rose-600 bg-rose-600 text-white" : "border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:border-zinc-700"}`}
            >
              <span className={`flex h-4 w-4 items-center justify-center rounded-full border ${form.shipping ? "border-white bg-white" : "border-zinc-600"}`}>{form.shipping && <Check className="h-3 w-3 text-rose-600" />}</span>
              Verzenden mogelijk
            </button>
          </div>
          {form.shipping && (
            <div className="mt-3 animate-fade-up">
              <label className="block text-xs text-zinc-500 mb-1">Verzendkosten (€) — laat leeg of 0 voor gratis verzending</label>
              <input data-testid="listing-shipping-cost-input" type="number" step="0.01" min="0" className={input} value={form.shipping_cost} onChange={upd("shipping_cost")} placeholder="bijv. 4.95" />
            </div>
          )}
          <input data-testid="listing-delivery-input" className={`${input} mt-3`} value={form.delivery} onChange={upd("delivery")} placeholder="Extra info, bijv. discrete verzending, alleen ophalen na 18u..." />
        </div>

        <div>
          <label className={label}>Hoe wil je contact?</label>
          <select data-testid="listing-contact-method-select" className={input} value={form.contact_method} onChange={upd("contact_method")}>
            <option value="chat">Alleen via de chat (geen nummer delen)</option>
            <option value="contact">Nummer/e-mail delen ná eerste chatbericht</option>
            <option value="both">Chat + nummer/e-mail delen na contact</option>
          </select>
          <p className="mt-2 text-xs text-zinc-500">Kopers nemen altijd eerst via de site-chat contact op. Je telefoonnummer/e-mail wordt pas zichtbaar in de chat nadat er contact is gelegd.</p>
        </div>

        {(form.contact_method === "contact" || form.contact_method === "both") && (
          <div className="grid grid-cols-2 gap-4 animate-fade-up">
            <div>
              <label className={label}>Contact e-mail</label>
              <input data-testid="listing-contact-email-input" type="email" className={input} value={form.contact_email} onChange={upd("contact_email")} placeholder="jij@email.nl" />
            </div>
            <div>
              <label className={label}>Contact telefoon</label>
              <input data-testid="listing-contact-phone-input" className={input} value={form.contact_phone} onChange={upd("contact_phone")} placeholder="06..." />
            </div>
          </div>
        )}

        <button
          data-testid="submit-listing-button"
          type="submit"
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-medium px-5 py-3.5 shadow-lg shadow-rose-950/50 transition-all hover:scale-[1.01] disabled:opacity-60"
        >
          {saving ? "Bezig..." : editing ? "Wijzigingen opslaan" : freeEligible ? "Plaats gratis advertentie" : "Plaats advertentie — betaal €1,00"}
        </button>
        <p className="text-center text-xs text-zinc-500">
          {editing
            ? "Je wijzigingen worden direct zichtbaar op je advertentie."
            : freeEligible
            ? "Je eerste advertentie is gratis. Volgende advertenties kosten €1,00 per stuk."
            : "Publiceren kost eenmalig €1,00. Je betaalt veilig met iDEAL of creditcard via Mollie."}
        </p>
        {!editing && !freeEligible && <PaymentMethods className="pt-1" />}
      </form>
    </div>
  );
}
